import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';
import { bankMovementKeys } from './useBankMovements';
import { supplierLedgerKeys } from './useSupplierLedger';

const paymentOrderKeys = {
  all: ['paymentOrders'],
  lists: (filters) => [...paymentOrderKeys.all, 'list', filters],
  detail: (id) => [...paymentOrderKeys.all, 'detail', id],
};

export const usePaymentOrders = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: paymentOrderKeys.lists(filters),
    queryFn: async () => {
      const response = await apiClient.get('payment_orders', {
        params: filters,
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    keepPreviousData: true,
    ...options,
  });
};

export const usePaymentOrder = (id, options = {}) => {
  return useQuery({
    queryKey: paymentOrderKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`payment_orders/${id}`);
      return response.data;
    },
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useCreatePaymentOrderWithMovements = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const response = await apiClient.post('payment_orders/with-movements', payload);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: paymentOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.all });
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.lists() });
      queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all });

      const paymentOrderUuid = data?.payment_order?.uuid;
      if (paymentOrderUuid) {
        queryClient.invalidateQueries({
          queryKey: bankMovementKeys.byPaymentOrder(paymentOrderUuid),
        });
      }

      return data;
    },
  });
};

export const useUpdatePaymentOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`payment_orders/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: paymentOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: paymentOrderKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.all });
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.lists() });
      queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all });
    },
  });
};

export const useUpdatePaymentOrderWithMovements = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`payment_orders/${id}/with-movements`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: paymentOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: paymentOrderKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.all });
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.lists() });
      queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all });
      queryClient.invalidateQueries({
        queryKey: bankMovementKeys.byPaymentOrder(variables.id),
      });
      const paymentOrderUuid = data?.payment_order?.uuid;
      if (paymentOrderUuid) {
        queryClient.invalidateQueries({
          queryKey: bankMovementKeys.byPaymentOrder(paymentOrderUuid),
        });
      }
    },
  });
};

export const useDeletePaymentOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`payment_orders/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.all });
      queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all });
    },
  });
};

export const usePrintPaymentOrder = () => {
  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.get(`payment_orders/${id}/print`);
      const { pdf_base64, download_name } = response.data;

      if (pdf_base64) {
        const byteCharacters = atob(pdf_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i += 1) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', download_name || 'orden_pago.pdf');
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      }

      return response.data;
    },
  });
};

export default paymentOrderKeys;
